import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { JsonView } from '~/app/components/common/JsonView/JsonView';
import { usePipelineJsonPanel } from './hooks/usePipelineJsonPanel';

const { Text } = Typography;

type PipelineJsonPanelProps = {
  title: string;
  hint?: string;
  value: unknown;
  emptyText?: string;
  editable?: boolean;
  disabled?: boolean;
  onChange?: (value: unknown) => void;
  onCopy: () => void;
  onDownload: () => void;
};

export function PipelineJsonPanel({
  title,
  hint,
  value,
  emptyText,
  editable = false,
  disabled = false,
  onChange,
  onCopy,
  onDownload,
}: PipelineJsonPanelProps) {
  const { t } = useTranslation('pipelineDebug');
  const { text, parseError, hasValue, setText, applyText, resetText } =
    usePipelineJsonPanel({ value, editable, disabled, onChange });

  return (
    <div className="pipeline-debug-json-panel">
      <Flex
        justify="space-between"
        align="flex-start"
        wrap
        gap={8}
        className="pipeline-debug-json-panel__header"
      >
        <Flex vertical gap={2}>
          <Text strong>{title}</Text>
          {hint ? (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {hint}
            </Text>
          ) : null}
        </Flex>
        <Space size={6} wrap>
          <Button
            size="small"
            icon={<CopyOutlined />}
            disabled={!hasValue}
            onClick={onCopy}
          >
            {t('actions.copy')}
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!hasValue}
            onClick={onDownload}
          >
            {t('actions.download')}
          </Button>
        </Space>
      </Flex>

      {editable ? (
        <Flex vertical gap={8}>
          <Input.TextArea
            value={text}
            rows={10}
            disabled={disabled}
            placeholder={emptyText}
            onChange={(event) => setText(event.target.value)}
            className="pipeline-debug-json-editor"
          />
          {parseError ? (
            <Text type="danger" style={{ fontSize: 13 }}>
              {parseError}
            </Text>
          ) : null}
          <Flex gap={8} wrap>
            <Button
              size="small"
              type="primary"
              disabled={disabled}
              onClick={() => {
                applyText();
              }}
            >
              {t('actions.applyChanges')}
            </Button>
            <Button size="small" disabled={disabled} onClick={resetText}>
              {t('actions.resetChanges')}
            </Button>
          </Flex>
        </Flex>
      ) : (
        <JsonView
          value={value}
          emptyText={emptyText}
          collapsed={2}
          maxHeight={220}
          enableClipboard={false}
        />
      )}
    </div>
  );
}
